import type { ReactNode } from 'react';
import { Button } from '@paige/ui';
import { CodeField } from './TotpEnrolment';
import { useCodeEntry } from './use-code-entry';
import type { Resend } from './use-resend';

/** The code Paige mailed, asked for before an authenticator app can be set up on an unconfirmed address. */
export function EmailConfirmStep({
  busy,
  error,
  onConfirm,
  resend,
  onCancel,
}: {
  busy: boolean;
  error: string | null;
  onConfirm: (code: string) => void;
  resend: Resend;
  onCancel?: (() => void) | undefined;
}): ReactNode {
  // As sign-in's code step: the box has the focus on arrival and the sixth digit sends the code.
  const { code, change, send, submit } = useCodeEntry(busy, error, onConfirm);
  return (
    <form className="totp" onSubmit={submit} noValidate>
      <div className="totp__how">
        <p>First, confirm your email. Paige sent a 6-digit code to your address.</p>
      </div>
      <CodeField
        code={code}
        error={resend.problem ?? error}
        onChange={change}
        onComplete={send}
        label="The code from the email"
        autoFocus
      />
      <p className="totp__said" role="status">
        {resend.said}
      </p>
      <Actions busy={busy} ready={code.length === 6} resend={resend} onCancel={onCancel} />
    </form>
  );
}

/** Not now, a new code (one request at a time), and Confirm. */
function Actions(props: {
  busy: boolean;
  ready: boolean;
  resend: Resend;
  onCancel: (() => void) | undefined;
}): ReactNode {
  const { busy, ready, resend, onCancel } = props;
  return (
    <div className="totp__actions">
      {onCancel && (
        <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
          Not now
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        onClick={resend.run}
        disabled={busy}
        loading={resend.busy}
      >
        Send a new code
      </Button>
      <Button type="submit" variant="primary" loading={busy} disabled={!ready}>
        Confirm
      </Button>
    </div>
  );
}
